import React, { useEffect, useState, useCallback } from "react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";
import { toast } from "components/ui/toast";
import getNlTemplate from "./nlTemplate";
import { Pencil, X, Trash2, CheckCircle, Copy } from "lucide-react";

interface EditNlProps {
    widget: any;
    index: number;
    apiKey: string;
    editWidget: (cfg: any, index: number) => void;
    saveWidget: (obj: any, index: number) => void;
    updateWidget: (obj: any, id: string) => void;
    deleteWidget: (id: string) => void;
}

const EditNl: React.FC<EditNlProps> = ({
    widget,
    index,
    apiKey,
    editWidget,
    saveWidget,
    updateWidget,
    deleteWidget,
}) => {
    const [nlTemplate, setNlTemplate] = useState<string>("");
    const [showModal, setShowModal] = useState(false);
    const [embedHtml, setEmbedHtml] = useState({ height: "", width: "" });
    const [startEdit, setStartEdit] = useState(false);
    const [edited, setEdited] = useState(false);

    const buildTemplate = useCallback(
        (cfg: any) => {
            const htmlString = getNlTemplate(cfg, apiKey, widget?.leadsGroup);
            const blob = new Blob([htmlString], { type: "text/html" });
            const url = URL.createObjectURL(blob);
            setNlTemplate(url);
        },
        [apiKey, widget?.leadsGroup]
    );

    const copyNLToClipboard = async () => {
        if (!widget.widget_url) return;
        const embedText = `<embed src="${widget.widget_url}" width="${embedHtml.width}" height="${embedHtml.height}">`;
        try {
            await navigator.clipboard.writeText(embedText);
            setShowModal(false);
            setEmbedHtml({ height: "", width: "" });
            toast.success("Widget copied successfully!");
        } catch (err) {
            console.error("❌ Failed to copy text:", err);
            toast.error("Failed to copy embed code");
        }
    };

    const saveNewsLetterData = useCallback(() => {
        const obj = {
            name: widget.name,
            tag_name: "NewsLetter",
            html_content: getNlTemplate(widget?.config, apiKey, widget?.leadsGroup),
            json_content: widget?.config,
        };
        if (widget.saved) updateWidget(obj, widget.id);
        else saveWidget(obj, index);
    }, [apiKey, widget, index, updateWidget, saveWidget]);

    const handleWidgetData = () => {
        setEdited(false);
        saveNewsLetterData();
    };

    useEffect(() => {
        buildTemplate(widget?.config);
    }, [widget?.config]);

    return (
        <div className="mt-3 w-full custom-new-card bg-surface mb-4 rounded-2xl capitalize">
            <h6 className="text-center text-lg font-semibold mb-2 w-full">{widget.name}</h6>

            <div className="rounded-2xl relative border border-line dark:border-gray-700 overflow-hidden">
                <iframe title="Newsletter preview" src={nlTemplate ? nlTemplate : undefined} className="w-full" style={{ height: 226 }} />

                {widget.saved && widget.widget_url && (
                    <>
                        <Button
                            size="icon"
                            variant="ghost"
                            className="absolute top-2 right-2 bg-blue-50 dark:bg-slate-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md"
                            onClick={() => setShowModal(true)}
                        >
                            <Copy />
                        </Button>

                        <Dialog open={showModal} onOpenChange={setShowModal}>
                            <DialogContent className="max-w-md">
                                <DialogHeader>
                                    <DialogTitle>Widget Config</DialogTitle>
                                </DialogHeader>

                                <div className="grid grid-cols-2 gap-4 py-3">
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Height</label>
                                        <Input
                                            type="number"
                                            placeholder="Enter Height"
                                            value={embedHtml.height}
                                            onChange={(e) =>
                                                setEmbedHtml((prev) => ({ ...prev, height: e.target.value }))
                                            }
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Width</label>
                                        <Input
                                            type="number"
                                            placeholder="Enter Width"
                                            value={embedHtml.width}
                                            onChange={(e) =>
                                                setEmbedHtml((prev) => ({ ...prev, width: e.target.value }))
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2 pt-4">
                                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                                        Close
                                    </Button>
                                    <Button
                                        disabled={!embedHtml.width || !embedHtml.height}
                                        onClick={copyNLToClipboard}
                                    >
                                        Copy
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </>
                )}
            </div>

            <div className="w-full flex justify-end mt-3 gap-2">
                <Button
                    variant="primary"
                    className="rounded-full"
                    onClick={() => setStartEdit((prev) => !prev)}
                >
                    {startEdit ? (
                        <>
                            <X className="h-4 w-4" /> Cancel
                        </>
                    ) : (
                        <>
                            <Pencil className="h-4 w-4" /> Edit
                        </>
                    )}
                </Button>

                {widget.saved && (
                    <Button
                        variant="destructive"
                        className="rounded-full"
                        onClick={() => deleteWidget(widget.id)}
                    >
                        <Trash2 className="h-4 w-4" /> Delete
                    </Button>
                )}

                {(edited || !widget.saved) && (
                    <Button
                        className="rounded-full"
                        onClick={handleWidgetData}
                    >
                        <CheckCircle className="h-4 w-4" /> Save
                    </Button>
                )}
            </div>

            {startEdit && widget?.config && (
                <div className="mt-3">
                    <div className="text-center text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">
                        Edit News Letter
                    </div>
                    <form className="space-y-3">
                        {/* === Heading === */}
                        <div className="font-semibold underline text-gray-700 dark:text-gray-300">Heading</div>
                        <div className="grid grid-cols-3 gap-2">
                            <div>
                                <label className="form-label">Text</label>
                                <Input
                                    value={widget?.config.heading.text}
                                    onChange={(e) => {
                                        const updatedWidget = {
                                            ...widget.config,
                                            heading: {
                                                ...widget.config.heading,
                                                text: e.target.value
                                            }
                                        }
                                        editWidget(updatedWidget, index);
                                        setEdited(true)
                                    }}
                                />
                            </div>
                            <div>
                                <label className="form-label">Color</label>
                                <Input
                                    type="color"
                                    value={widget?.config?.heading?.color}
                                    onChange={(e) => {
                                        const updatedWidget = {
                                            ...widget.config,
                                            heading: {
                                                ...widget.config.heading,
                                                color: e.target.value
                                            }
                                        };

                                        editWidget(updatedWidget, index);
                                        setEdited(true)
                                    }}
                                />
                            </div>
                            <div>
                                <label className="form-label">Font Size</label>
                                <Input
                                    type="number"
                                    value={widget?.config?.heading?.size}
                                    onChange={(e) => {
                                        const updatedWidget = {
                                            ...widget.config,
                                            heading: {
                                                ...widget.config.heading,
                                                size: Number(e.target.value)
                                            }
                                        };
                                        editWidget(updatedWidget, index);
                                        setEdited(true)
                                    }}
                                />
                            </div>
                        </div>

                        {/* === Subheading === */}
                        <div className="font-semibold underline text-gray-700 dark:text-gray-300 mt-2">
                            Sub Heading
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <div>
                                <label className="form-label">Text</label>
                                <Input
                                    value={widget?.config?.subHeading?.text || ""}
                                    onChange={(e) => {
                                        const updatedWidget = {
                                            ...widget.config,
                                            subHeading: {
                                                ...widget.config.subHeading,
                                                text: e.target.value
                                            }
                                        };
                                        editWidget(updatedWidget, index);
                                        setEdited(true)
                                    }}
                                />
                            </div>
                            <div>
                                <label className="form-label">Color</label>
                                <Input
                                    type="color"
                                    value={widget?.config?.subHeading?.color}
                                    onChange={(e) => {
                                        const updatedWidget = {
                                            ...widget.config,
                                            subHeading: {
                                                ...widget.config.subHeading,
                                                color: e.target.value
                                            }
                                        };

                                        editWidget(updatedWidget, index);
                                        setEdited(true)
                                    }}
                                />
                            </div>
                            <div>
                                <label className="form-label">Font Size</label>
                                <Input
                                    type="number"
                                    value={widget?.config?.subHeading?.size}
                                    onChange={(e) => {
                                        const updatedWidget = {
                                            ...widget.config,
                                            subHeading: {
                                                ...widget.config.subHeading,
                                                size: Number(e.target.value)
                                            }
                                        };
                                        editWidget(updatedWidget, index);
                                        setEdited(true)
                                    }}
                                />
                            </div>
                        </div>

                        {/* === Background === */}
                        <div className="font-semibold underline text-gray-700 dark:text-gray-300 mt-2">
                            Background
                        </div>
                        <div>
                            <label className="form-label">Background Color</label>
                            <Input
                                type="color"
                                value={widget?.config?.background || ""}
                                onChange={(e) => {
                                    const updatedWidget = {
                                        ...widget.config,
                                        background: e.target.value,
                                    };
                                    // Update the widget configuration
                                    editWidget(updatedWidget, index);
                                    setEdited(true)
                                }}
                            />
                        </div>

                        {/* === Input === */}
                        <div className="font-semibold underline text-gray-700 dark:text-gray-300 mt-2">Input</div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="form-label">Text</label>
                                <Input
                                    value={widget?.config?.input?.text || ""}
                                    onChange={(e) => {
                                        const updatedWidget = {

                                            ...widget.config,
                                            input: {
                                                ...widget.config.input,
                                                text: e.target.value,
                                            },
                                        };
                                        // Update the widget configuration
                                        editWidget(updatedWidget, index);
                                        setEdited(true)
                                    }}
                                />
                            </div>
                            <div>
                                <label className="form-label">Width</label>
                                <Input
                                    type="number"
                                    value={widget?.config?.input?.width || ""}
                                    onChange={(e) => {
                                        const updatedWidget = {
                                            ...widget.config,
                                            input: {
                                                ...widget.config.input,
                                                width: Number(e.target.value),
                                            },
                                        };
                                        // Update the widget configuration
                                        editWidget(updatedWidget, index);
                                        setEdited(true)
                                    }}
                                />
                            </div>
                        </div>

                        {/* === Button === */}
                        <div className="font-semibold underline text-gray-700 dark:text-gray-300 mt-2">Button</div>
                        <div className="grid grid-cols-3 gap-2">
                            <div>
                                <label className="form-label">Text</label>
                                <Input
                                    value={widget?.config?.button?.text || ""}
                                    onChange={(e) => {
                                        const updatedWidget = {
                                            ...widget.config,
                                            button: { ...widget.config.button, text: e.target.value },
                                        };
                                        // Update the widget configuration using editWidget
                                        editWidget(updatedWidget, index);
                                        setEdited(true)
                                    }}
                                />
                            </div>
                            <div>
                                <label className="form-label">Color</label>
                                <Input
                                    type="color"
                                    value={widget?.config?.button?.color}
                                    onChange={(e) => {
                                        const updatedWidget = {
                                            ...widget.config,
                                            button: { ...widget.config.button, color: e.target.value },
                                        };
                                        editWidget(updatedWidget, index);
                                        setEdited(true)
                                    }}
                                />
                            </div>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default EditNl;
